/**
 * @fileoverview Walkthrough Dialog
 * @description Modal-based multi-step walkthrough component.
 * Used for wizard-style flows (e.g., post-organization-creation setup).
 * Inspired by Notion's onboarding hub and Linear's focused walkthroughs.
 * @module features/walkthrough/components/WalkthroughDialog
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Lightbulb,
  CheckCircle2,
  Sparkles,
  Building2,
  Users,
  FolderKanban,
  Settings,
  Plug,
  Trophy,
  DollarSign,
  Target,
  Link,
  MapPin,
  BarChart3,
  TrendingUp,
  Columns3,
  List,
  Orbit,
  Layers,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@flowstate/utils';
import type { WalkthroughStep } from '../types';

const ICON_MAP: Record<string, LucideIcon> = {
  building: Building2,
  users: Users,
  folder: FolderKanban,
  settings: Settings,
  plug: Plug,
  sparkles: Sparkles,
  lightbulb: Lightbulb,
  check: CheckCircle2,
  trophy: Trophy,
  dollar: DollarSign,
  target: Target,
  link: Link,
  'map-pin': MapPin,
  'bar-chart': BarChart3,
  'trending-up': TrendingUp,
  columns: Columns3,
  list: List,
  orbit: Orbit,
  layers: Layers,
};

interface WalkthroughDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Current step to display */
  step: WalkthroughStep;
  /** All steps for the checklist sidebar */
  steps: WalkthroughStep[];
  /** Current step index */
  stepIndex: number;
  /** Total steps count */
  totalSteps: number;
  /** Completed step IDs */
  completedSteps: string[];
  /** Handler for next step */
  onNext: () => void;
  /** Handler for previous step */
  onPrev: () => void;
  /** Handler for dismiss */
  onDismiss: () => void;
  /** Handler for complete */
  onComplete: () => void;
  /** Whether this is the last step */
  isLastStep: boolean;
  /** Whether this is the first step */
  isFirstStep: boolean;
  /** Walkthrough name for the header */
  walkthroughName?: string;
}

export function WalkthroughDialog({
  open,
  step,
  steps,
  stepIndex,
  totalSteps,
  completedSteps,
  onNext,
  onPrev,
  onDismiss,
  onComplete,
  isLastStep,
  isFirstStep,
  walkthroughName,
}: WalkthroughDialogProps) {
  const progressPercent = ((stepIndex + 1) / totalSteps) * 100;
  const IconComponent = step.icon ? ICON_MAP[step.icon] : Sparkles;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onDismiss()}>
      <DialogContent
        hideCloseButton
        hideDefaultDescription
        className="z-[9999] sm:max-w-[640px] p-0 gap-0 overflow-hidden top-1/2 my-0 -translate-y-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]"
      >
        <div className="flex min-h-[420px]">
          {/* Left sidebar - step checklist */}
          <div className="hidden sm:flex w-[200px] flex-col bg-muted/30 border-r p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Steps
            </p>
            <nav className="flex flex-col gap-1" aria-label="Walkthrough steps">
              {steps.map((s, idx) => {
                const isCompleted = completedSteps.includes(s.id);
                const isCurrent = idx === stepIndex;
                const StepIcon = s.icon ? ICON_MAP[s.icon] : null;

                return (
                  <div
                    key={s.id}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors',
                      isCurrent && 'bg-primary/10 text-primary font-medium',
                      isCompleted && !isCurrent && 'text-muted-foreground',
                      !isCurrent && !isCompleted && 'text-muted-foreground/60'
                    )}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                    ) : StepIcon ? (
                      <StepIcon className="h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <div
                        className={cn(
                          'h-3.5 w-3.5 rounded-full border shrink-0 flex items-center justify-center text-[9px]',
                          isCurrent ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30'
                        )}
                      >
                        {!isCurrent && idx + 1}
                      </div>
                    )}
                    <span className="truncate">{s.title}</span>
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Main content area */}
          <div className="flex flex-1 flex-col">
            <DialogHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {walkthroughName && (
                    <span className="font-medium">{walkthroughName}</span>
                  )}
                  <span>Step {stepIndex + 1} of {totalSteps}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={onDismiss}
                  aria-label="Dismiss walkthrough"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Progress value={progressPercent} className="h-1.5 mt-2" />
            </DialogHeader>

            <div className="flex-1 px-6 pb-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  {IconComponent && <IconComponent className="h-6 w-6 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-lg mb-2">{step.title}</DialogTitle>
                  <DialogDescription className="text-sm leading-relaxed">
                    {step.description}
                  </DialogDescription>
                </div>
              </div>

              {step.bestPractice && (
                <div className="mt-5 flex items-start gap-2.5 rounded-lg bg-primary/5 p-3 border border-primary/10">
                  <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-primary mb-0.5">Best Practice</p>
                    <p className="text-xs text-primary/80">{step.bestPractice}</p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="px-6 pb-6 flex items-center justify-between border-t pt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="text-muted-foreground"
              >
                Skip for now
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onPrev}
                  disabled={isFirstStep}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>

                {isLastStep ? (
                  <Button size="sm" onClick={onComplete} className="gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    {step.actionLabel || 'Get Started'}
                  </Button>
                ) : (
                  <Button size="sm" onClick={onNext} className="gap-1">
                    {step.actionLabel || 'Next'}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
